import { Badge, Box, Heading, Text } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { Fragment, useMemo } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { useEhrContext } from "../../../shared-logic/ehr-context/ehr-context";
import { BaseTableLayout } from "../../components/base-table-layout";
import { HighlightedText } from "../../components/highlighted-text";
import { NoteSnippetsDrawer } from "../../components/note-snippets-drawer";
import { TableFilterPopover } from "../../components/table-filter-popover";
import { useTableFilters } from "../../hooks/use-table-filters";
import { createImagingReportFilters } from "../../utils/imaging-report-filters";
import { createNoteMatchData } from "../../utils/note-processing";
import { TableRow } from "../../utils/tables-metadata";
import { ImagingReportRowData } from "./imaging-reports-rows";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";

export type ImagingReportsTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  displayName: string;
  onPdfView?: (docId: string) => void;
};

/**
 * Table component for displaying imaging reports.
 * Has two rendering modes:
 * - Standard table when no search query
 * - Card-style layout with note snippets when searching
 */
export function ImagingReportsTable({
  rows,
  onRowClick,
  filterQuery,
  displayName,
}: ImagingReportsTableProps) {
  const filterOptions = useMemo(() => {
    const imagingRows = rows as ImagingReportRowData[];
    const filters = createImagingReportFilters(imagingRows);

    return filters.map(option => ({
      label: option.label,
      groupLabel: option.groupLabel,
      isChecked: true, // All filters enabled by default
      filterFn: (rowData: ImagingReportRowData) => {
        const parts = option.value.split(":");
        const type = parts[0];
        const value = parts[1];
        if (!type || !value) return true;

        switch (type) {
          case "imagingModality":
            if (value === "undefined") {
              return !rowData.imagingModality || rowData.imagingModality === "-";
            }
            // Check if the comma-separated list contains the value
            if (!rowData.imagingModality || rowData.imagingModality === "-") return false;
            return rowData.imagingModality
              .split(",")
              .map(v => v.trim())
              .includes(value);
          case "bodyRegion":
            if (value === "undefined") {
              return !rowData.bodyRegion || rowData.bodyRegion === "-";
            }
            // Check if the comma-separated list contains the value
            if (!rowData.bodyRegion || rowData.bodyRegion === "-") return false;
            return rowData.bodyRegion
              .split(",")
              .map(v => v.trim())
              .includes(value);
          default:
            return true;
        }
      },
    }));
  }, [rows]);

  const { activeFilters, setActiveFilters, filteredRows } = useTableFilters(rows, filterOptions);

  function handleRowClick(row: ImagingReportRowData) {
    onRowClick(row);
  }

  // Check if row has note matches
  function hasNoteMatches(row: TableRow): boolean {
    const noteData = row.noteData as { fullText: string } | undefined;
    if (!noteData?.fullText || !filterQuery.trim()) return false;
    const matchData = createNoteMatchData(noteData.fullText, filterQuery);
    return Boolean(matchData && matchData.count > 0);
  }

  const hasSearchQuery = Boolean(filterQuery.trim());

  const ehrContext = useEhrContext();

  const columns = useMemo<ColumnDef<ImagingReportRowData>[]>(() => {
    const cols: ColumnDef<ImagingReportRowData>[] = [
      { key: "imagingDisplay", header: "Imaging Display", maxWidth: "300px" },
      { key: "imagingModality", header: "Imaging Modality", maxWidth: "200px" },
      { key: "bodyRegion", header: "Body Region" },
      { key: "location", header: "Location" },
      { key: "date", header: "Date" },
    ];

    const ehrColumn = createEhrWriteBackColumn<ImagingReportRowData>(ehrContext, "note");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext]);

  if (!hasSearchQuery) {
    return (
      <BaseTableLayout
        title={displayName}
        rows={filteredRows as ImagingReportRowData[]}
        columns={columns}
        filterQuery={filterQuery}
        onRowClick={handleRowClick}
        headerActionsSlot={
          filterOptions.length > 0 ? (
            <TableFilterPopover
              filters={filterOptions}
              activeFilters={activeFilters}
              setActiveFilters={newFilters => setActiveFilters(newFilters)}
            />
          ) : undefined
        }
      />
    );
  }

  // Card-style layout with note snippets when searching
  return (
    <Box
      w="100%"
      mb={2}
      borderRadius="lg"
      bg="bg.surface"
      px={6}
      py={6}
      border="1px"
      borderColor="border"
      boxShadow="sm"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md" fontWeight="semibold" color="fg">
          {displayName} ({filteredRows.length})
        </Heading>
        {filterOptions.length > 0 && (
          <TableFilterPopover
            filters={filterOptions}
            activeFilters={activeFilters}
            setActiveFilters={newFilters => setActiveFilters(newFilters)}
          />
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {filteredRows.map(row => {
          const imagingRow = row as unknown as ImagingReportRowData;
          const hasMatches = hasNoteMatches(row);

          return (
            <Fragment key={row.id}>
              <Box
                borderRadius="md"
                borderWidth="1px"
                borderColor="border"
                boxShadow="sm"
                overflow="hidden"
                bg="bg.canvas"
              >
                {/* Main row - clickable */}
                <Box
                  cursor="pointer"
                  onClick={() => handleRowClick(imagingRow)}
                  _hover={{ bg: "interactive.hover" }}
                  p={4}
                >
                  <Box display="grid" gridTemplateColumns="repeat(5, 1fr)" gap={4}>
                    <Box>
                      {hasMatches ? (
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Text fontSize="sm" fontWeight="semibold" color="fg">
                            <HighlightedText
                              text={imagingRow.imagingModality}
                              searchQuery={filterQuery}
                            />
                          </Text>
                          <Badge
                            size="xs"
                            p={0}
                            bg="transparent"
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                            w="fit-content"
                            fontSize="2xs"
                            opacity={0.7}
                          >
                            Note excerpts below
                          </Badge>
                        </Box>
                      ) : (
                        <Text fontSize="sm" color="fg">
                          <HighlightedText
                            text={imagingRow.imagingModality}
                            searchQuery={filterQuery}
                          />
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText text={imagingRow.bodyRegion} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText
                          text={imagingRow.associatingDiagnosis}
                          searchQuery={filterQuery}
                        />
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText text={imagingRow.location} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText text={imagingRow.date} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                  </Box>
                </Box>

                {/* Note snippets drawer */}
                {hasMatches && (
                  <NoteSnippetsDrawer
                    noteData={row.noteData as { fullText: string } | undefined}
                    filterQuery={filterQuery}
                    rowId={row.id}
                    onSnippetClick={() => handleRowClick(imagingRow)}
                  />
                )}
              </Box>
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
